import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';

const OrderDetails = () => {
  const { orderId } = useParams();
  const { isAuthenticated } = useCustomerAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Return request states
  const [returnRequestInfo, setReturnRequestInfo] = useState(null);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState('');
  const [returnNotes, setReturnNotes] = useState('');
  const [submittingReturn, setSubmittingReturn] = useState(false);

  useEffect(() => {
    if (orderId && isAuthenticated) {
      fetchOrderDetails();
      fetchReturnRequestInfo();
    }
  }, [orderId, isAuthenticated]);

  const fetchReturnRequestInfo = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/return-requests/order/${orderId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setReturnRequestInfo(data);
      }
    } catch (err) {
      console.error('Error fetching return request info:', err);
    }
  };

  const handleSubmitReturnRequest = async () => {
    if (!returnReason) {
      alert('Please select a reason for your return request');
      return;
    }

    try {
      setSubmittingReturn(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/return-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          orderId,
          reason: returnReason,
          customerNotes: returnNotes
        })
      });

      if (response.ok) {
        const data = await response.json();
        setShowReturnModal(false);
        setReturnReason('');
        setReturnNotes('');
        fetchReturnRequestInfo();
        alert('Return request submitted successfully!');
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to submit return request');
      }
    } catch (err) {
      console.error('Error submitting return request:', err);
      alert('Failed to submit return request. Please try again.');
    } finally {
      setSubmittingReturn(false);
    }
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/customer/orders/${orderId}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setOrder(data.order);
      } else {
        setError('Order not found');
      }
    } catch (err) {
      setError('Failed to fetch order details');
      console.error('Error fetching order details:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'pending': '#F59E0B',
      'processing': '#3B82F6',
      'shipped': '#8B5CF6',
      'delivered': '#10B981',
      'cancelled': '#EF4444',
      'refunded': '#6B7280'
    };
    return statusColors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <polyline points="12,6 12,12 16,14"/>
        </svg>
      ),
      'processing': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      ),
      'shipped': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M16 3h5v5l-2 2-3-3-3 3-2-2V3z"/>
          <path d="M9 12l2 2 4-4"/>
          <path d="M21 8v13H3V8"/>
        </svg>
      ),
      'delivered': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4"/>
          <path d="M21 12c.552 0 1-.448 1-1V5c0-.552-.448-1-1-1H3c-.552 0-1 .448-1 1v6c0 .552.448 1 1 1h18z"/>
          <path d="M3 12v6c0 .552.448 1 1 1h16c.552 0 1-.448 1-1v-6"/>
        </svg>
      ),
      'cancelled': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
      ),
      'refunded': (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
          <path d="M21 3v5h-5"/>
          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
          <path d="M3 21v-5h5"/>
        </svg>
      )
    };
    return icons[status] || icons['pending'];
  };

  const getStatusDescription = (status) => {
    const descriptions = {
      'pending': 'Your order is being reviewed and will be processed soon.',
      'processing': 'Your order is being prepared for shipment.',
      'shipped': 'Your order has been shipped and is on its way.',
      'delivered': 'Your order has been successfully delivered.',
      'cancelled': 'Your order has been cancelled.',
      'refunded': 'Your order has been refunded.'
    };
    return descriptions[status] || 'Order status unknown.';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '16px'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #F3F4F6',
            borderTop: '4px solid #0D7463',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading order details...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        textAlign: 'center'
      }}>
        <div style={{
          width: '64px',
          height: '64px',
          backgroundColor: '#FEF2F2',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginBottom: '16px'
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
        </div>
        <h2 style={{ color: '#111827', marginBottom: '8px' }}>Order Not Found</h2>
        <p style={{ color: '#6B7280', marginBottom: '24px' }}>{error || 'The order you\'re looking for doesn\'t exist.'}</p>
        <button
          onClick={() => navigate('/customer/dashboard/orders')}
          style={{
            padding: '12px 24px',
            backgroundColor: '#0D7463',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Back to Orders
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>
      {/* Order Header */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: '700',
              color: '#111827',
              margin: '0 0 8px 0'
            }}>
              Order #{order.orderNumber || order._id}
            </h1>
            <p style={{
              fontSize: '14px',
              color: '#6B7280',
              margin: 0
            }}>
              Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            backgroundColor: '#F0FDF4',
            borderRadius: '6px',
            border: `1px solid ${getStatusColor(order.status)}20`
          }}>
            <div style={{ color: getStatusColor(order.status) }}>
              {getStatusIcon(order.status)}
            </div>
            <span style={{
              fontSize: '14px',
              fontWeight: '600',
              color: getStatusColor(order.status),
              textTransform: 'capitalize'
            }}>
              {order.status}
            </span>
          </div>
        </div>

        <div style={{
          padding: '16px',
          backgroundColor: '#F9FAFB',
          borderRadius: '6px',
          border: '1px solid #E5E7EB'
        }}>
          <p style={{
            fontSize: '14px',
            color: '#374151',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <div style={{ color: getStatusColor(order.status) }}>
              {getStatusIcon(order.status)}
            </div>
            {getStatusDescription(order.status)}
          </p>
        </div>
      </div>

      {/* Order Items */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB'
      }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 16px 0'
        }}>
          Order Items ({order.items?.length || 0})
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {order.items?.map((item, index) => (
            <div key={index} style={{
              display: 'flex',
              gap: '16px',
              padding: '16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '6px',
              border: '1px solid #E5E7EB'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#F3F4F6',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {item.product?.images?.[0] ? (
                  <img
                    src={item.product.images[0]}
                    alt={item.product.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px'
                    }}
                  />
                ) : (
                  <div style={{
                    color: '#9CA3AF',
                    fontSize: '12px',
                    textAlign: 'center'
                  }}>
                    No Image
                  </div>
                )}
              </div>
              
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: '0 0 4px 0'
                }}>
                  {item.name || item.product?.name || 'Product'}
                </h3>
                <p style={{
                  fontSize: '14px',
                  color: '#6B7280',
                  margin: '0 0 8px 0'
                }}>
                  Quantity: {item.qty || item.quantity || 1}
                </p>
                <p style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: '#111827',
                  margin: 0
                }}>
                  ₦{((item.price || 0) * (item.qty || item.quantity || 1)).toLocaleString()}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Order Summary */}
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #E5E7EB'
      }}>
        <h2 style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#111827',
          margin: '0 0 16px 0'
        }}>
          Order Summary
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '8px 0'
          }}>
            <span style={{ fontSize: '14px', color: '#6B7280' }}>Subtotal</span>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
              ₦{order.subtotal?.toLocaleString() || '0'}
            </span>
          </div>
          
          {order.shippingFee > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0'
            }}>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Shipping</span>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                ₦{order.shippingFee?.toLocaleString() || '0'}
              </span>
            </div>
          )}
          
          {order.tax > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0'
            }}>
              <span style={{ fontSize: '14px', color: '#6B7280' }}>Tax</span>
              <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                ₦{order.tax?.toLocaleString() || '0'}
              </span>
            </div>
          )}
          
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 0',
            borderTop: '1px solid #E5E7EB',
            marginTop: '8px'
          }}>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>Total</span>
            <span style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>
              ₦{order.total?.toLocaleString() || '0'}
            </span>
          </div>
        </div>
      </div>

      {/* Shipping Information */}
      {order.shippingInfo && (
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #E5E7EB'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0'
          }}>
            Shipping Information
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px'
          }}>
            <div>
              <label style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Name
              </label>
              <p style={{
                fontSize: '14px',
                color: '#111827',
                margin: '4px 0 0 0'
              }}>
                {order.shippingInfo.firstName} {order.shippingInfo.lastName}
              </p>
            </div>
            
            <div>
              <label style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Phone
              </label>
              <p style={{
                fontSize: '14px',
                color: '#111827',
                margin: '4px 0 0 0'
              }}>
                {order.shippingInfo.phone}
              </p>
            </div>
            
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#6B7280',
                textTransform: 'uppercase',
                letterSpacing: '0.05em'
              }}>
                Address
              </label>
              <p style={{
                fontSize: '14px',
                color: '#111827',
                margin: '4px 0 0 0'
              }}>
                {order.shippingInfo.address}<br />
                {order.shippingInfo.city}, {order.shippingInfo.state} {order.shippingInfo.zipCode}<br />
                {order.shippingInfo.country}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Delivery Tracking Information */}
      {(order.rider || order.deliveryTracking) && (
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #E5E7EB'
        }}>
          <h2 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            {order.deliveryTracking?.method === 'CHOWDECK' ? '⚡' : '🚚'} Delivery Information
          </h2>

          <div style={{
            padding: '16px',
            backgroundColor: order.deliveryTracking?.method === 'CHOWDECK' ? '#FFF7ED' : '#F0F9FF',
            borderRadius: '8px',
            border: `1px solid ${order.deliveryTracking?.method === 'CHOWDECK' ? '#FDBA74' : '#BAE6FD'}`
          }}>
            {/* Rider/Driver Info */}
            {(order.rider?.name || order.deliveryTracking?.rider?.name) && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '15px', fontWeight: '600', color: '#111827' }}>
                    {order.rider?.name || order.deliveryTracking?.rider?.name}
                  </span>
                  {(order.rider?.company || order.deliveryTracking?.provider) && (
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      backgroundColor: order.deliveryTracking?.method === 'CHOWDECK' ? '#F97316' : '#0EA5E9',
                      color: 'white',
                      borderRadius: '12px'
                    }}>
                      {order.rider?.company || order.deliveryTracking?.provider}
                    </span>
                  )}
                </div>
                {(order.rider?.phone || order.deliveryTracking?.rider?.phone) && (
                  <a
                    href={`tel:${order.rider?.phone || order.deliveryTracking?.rider?.phone}`}
                    style={{
                      fontSize: '14px',
                      color: '#0369A1',
                      textDecoration: 'none'
                    }}
                  >
                    📞 {order.rider?.phone || order.deliveryTracking?.rider?.phone}
                  </a>
                )}
              </div>
            )}

            {/* Tracking Info */}
            {(order.rider?.trackingCode || order.deliveryTracking?.rider?.trackingCode) && (
              <div style={{
                padding: '12px',
                backgroundColor: 'white',
                borderRadius: '6px',
                marginTop: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#6B7280' }}>Tracking Code:</span>
                  <span style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827',
                    fontFamily: 'monospace'
                  }}>
                    {order.rider?.trackingCode || order.deliveryTracking?.rider?.trackingCode}
                  </span>
                </div>
                {(order.rider?.trackingUrl || order.deliveryTracking?.rider?.trackingUrl || order.deliveryTracking?.trackingUrl) && (
                  <a
                    href={order.rider?.trackingUrl || order.deliveryTracking?.rider?.trackingUrl || order.deliveryTracking?.trackingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '8px',
                      padding: '8px 16px',
                      backgroundColor: '#0D7463',
                      color: 'white',
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      textDecoration: 'none'
                    }}
                  >
                    Track Package ↗
                  </a>
                )}
              </div>
            )}

            {/* Chowdeck specific - waiting for rider */}
            {order.deliveryTracking?.method === 'CHOWDECK' && !order.rider?.name && !order.deliveryTracking?.rider?.name && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: '#22C55E',
                  color: 'white',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {order.deliveryTracking?.status || 'PROCESSING'}
                </span>
                <span style={{ fontSize: '13px', color: '#6B7280' }}>
                  Chowdeck rider will be assigned soon
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Return Request Status or Button */}
      {order.status === 'delivered' && returnRequestInfo && (
        <div style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '8px',
          padding: '24px',
          marginBottom: '24px',
          border: '1px solid #E5E7EB'
        }}>
          {returnRequestInfo.hasReturnRequest ? (
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 16px 0'
              }}>
                Return Request Status
              </h2>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '16px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                border: '1px solid #E5E7EB'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: returnRequestInfo.returnRequest.status === 'PENDING' ? '#FEF3C7' :
                    returnRequestInfo.returnRequest.status === 'MERCHANT_APPROVED' ? '#D1FAE5' :
                    returnRequestInfo.returnRequest.status === 'MERCHANT_REJECTED' ? '#FEE2E2' :
                    returnRequestInfo.returnRequest.status === 'RESOLVED' ? '#DBEAFE' : '#F3F4F6',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke={returnRequestInfo.returnRequest.status === 'PENDING' ? '#D97706' :
                      returnRequestInfo.returnRequest.status === 'MERCHANT_APPROVED' ? '#059669' :
                      returnRequestInfo.returnRequest.status === 'MERCHANT_REJECTED' ? '#DC2626' :
                      returnRequestInfo.returnRequest.status === 'RESOLVED' ? '#2563EB' : '#6B7280'}
                    strokeWidth="2">
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                    <path d="M21 3v5h-5"/>
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                    <path d="M3 21v-5h5"/>
                  </svg>
                </div>
                <div>
                  <p style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#111827',
                    margin: '0 0 4px 0'
                  }}>
                    Request #{returnRequestInfo.returnRequest.requestNumber}
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#6B7280',
                    margin: 0
                  }}>
                    Status: <span style={{
                      fontWeight: '500',
                      color: returnRequestInfo.returnRequest.status === 'PENDING' ? '#D97706' :
                        returnRequestInfo.returnRequest.status === 'MERCHANT_APPROVED' ? '#059669' :
                        returnRequestInfo.returnRequest.status === 'MERCHANT_REJECTED' ? '#DC2626' :
                        returnRequestInfo.returnRequest.status === 'RESOLVED' ? '#2563EB' : '#6B7280'
                    }}>
                      {returnRequestInfo.returnRequest.status.replace('_', ' ')}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          ) : returnRequestInfo.returnPolicy?.enabled ? (
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 16px 0'
              }}>
                Return Policy
              </h2>
              <div style={{
                padding: '16px',
                backgroundColor: '#F0FDF4',
                borderRadius: '6px',
                border: '1px solid #BBF7D0',
                marginBottom: '16px'
              }}>
                <p style={{
                  fontSize: '14px',
                  color: '#166534',
                  margin: '0 0 12px 0',
                  fontWeight: '500'
                }}>
                  This order is eligible for returns within {returnRequestInfo.returnPolicy.returnWindowDays} days of delivery.
                </p>
                {returnRequestInfo.returnPolicy.returnConditions && returnRequestInfo.returnPolicy.returnConditions.length > 0 && (
                  <div>
                    <p style={{ fontSize: '13px', color: '#166534', margin: '0 0 8px 0', fontWeight: '600' }}>
                      Return Conditions:
                    </p>
                    <ul style={{
                      margin: 0,
                      paddingLeft: '20px',
                      fontSize: '13px',
                      color: '#166534'
                    }}>
                      {returnRequestInfo.returnPolicy.returnConditions.map((condition, idx) => (
                        <li key={idx} style={{ marginBottom: '4px' }}>{condition}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {returnRequestInfo.returnPolicy.policyText && (
                  <p style={{
                    fontSize: '13px',
                    color: '#166534',
                    margin: '12px 0 0 0',
                    fontStyle: 'italic'
                  }}>
                    {returnRequestInfo.returnPolicy.policyText}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowReturnModal(true)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
                  <path d="M21 3v5h-5"/>
                  <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
                  <path d="M3 21v-5h5"/>
                </svg>
                Request Return
              </button>
            </div>
          ) : (
            <div style={{
              padding: '16px',
              backgroundColor: '#FEF2F2',
              borderRadius: '6px',
              border: '1px solid #FECACA'
            }}>
              <p style={{
                fontSize: '14px',
                color: '#991B1B',
                margin: 0
              }}>
                Returns are not currently available for this store.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'flex-end'
      }}>
        <button
          onClick={() => navigate('/customer/dashboard/orders')}
          style={{
            padding: '12px 24px',
            backgroundColor: 'transparent',
            color: '#6B7280',
            border: '1px solid #D1D5DB',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer'
          }}
        >
          Back to Orders
        </button>

        {order.status === 'delivered' && (
          <button
            style={{
              padding: '12px 24px',
              backgroundColor: '#0D7463',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Reorder
          </button>
        )}
      </div>

      {/* Return Request Modal */}
      {showReturnModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#111827',
                margin: 0
              }}>
                Request Return
              </h2>
              <button
                onClick={() => setShowReturnModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            {/* Return Policy Info in Modal */}
            {returnRequestInfo?.returnPolicy && (
              <div style={{
                padding: '12px',
                backgroundColor: '#F0FDF4',
                borderRadius: '6px',
                marginBottom: '20px',
                border: '1px solid #BBF7D0'
              }}>
                <p style={{
                  fontSize: '13px',
                  color: '#166534',
                  margin: 0
                }}>
                  Returns must be requested within {returnRequestInfo.returnPolicy.returnWindowDays} days of delivery.
                </p>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Reason for Return *
              </label>
              <select
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px',
                  color: '#374151'
                }}
              >
                <option value="">Select a reason</option>
                <option value="DEFECTIVE">Defective/Damaged</option>
                <option value="WRONG_ITEM">Wrong Item Received</option>
                <option value="NOT_AS_DESCRIBED">Not as Described</option>
                <option value="CHANGED_MIND">Changed Mind</option>
                <option value="SIZE_FIT_ISSUE">Size/Fit Issue</option>
                <option value="QUALITY_ISSUE">Quality Issue</option>
                <option value="ARRIVED_LATE">Arrived Too Late</option>
                <option value="OTHER">Other</option>
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Additional Notes (Optional)
              </label>
              <textarea
                value={returnNotes}
                onChange={(e) => setReturnNotes(e.target.value)}
                placeholder="Please provide any additional details about your return request..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #D1D5DB',
                  fontSize: '14px',
                  color: '#374151',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowReturnModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitReturnRequest}
                disabled={submittingReturn || !returnReason}
                style={{
                  padding: '12px 24px',
                  backgroundColor: submittingReturn || !returnReason ? '#D1D5DB' : '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: submittingReturn || !returnReason ? 'not-allowed' : 'pointer'
                }}
              >
                {submittingReturn ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
